import { paintSceneArtwork } from './36-game-artwork.mjs';
import { gameView } from '../../../packages/domain/src/50-game-engine.mjs';
import { pebbleBalanceGeometry } from './60-pebble-artwork.mjs';

const supported = new Set([
  'sand-layers',
  'mosaic-paint',
  'pattern-stamp',
  'ripple-garden',
  'thread-loom',
  'cloud-shapes',
  'paper-fold',
  'pebble-balance',
]);

/** Accepted creative actions decorate stable artwork, never move hit targets or
 * infer completion from decoration. Every effect has bounded lifetime and one
 * cancellable owner; reduced motion exposes the final accepted artwork directly. */
export function attachCreativeFeedback(ctx, board, scene) {
  const animations = new Set(),
    temporary = new Set();
  let disposed = false,
    sandGeneration = 0;
  function cancel() {
    sandGeneration++;
    for (const animation of animations) animation.cancel();
    animations.clear();
    for (const node of temporary) node.remove();
    temporary.clear();
  }
  function animate(node, frames, duration, delay = 0, easing = 'ease-out') {
    if (!node || typeof node.animate !== 'function') return;
    node.style.transformBox = 'fill-box';
    node.style.transformOrigin = 'center';
    const animation = node.animate(frames, {
      duration,
      delay,
      easing,
      fill: 'backwards',
    });
    animations.add(animation);
    void animation.finished.then(
      () => animations.delete(animation),
      () => animations.delete(animation),
    );
    return animation;
  }
  function cell(index) {
    return board.querySelector(`[data-cell="${index}"] .game-piece-art`);
  }
  function glow(host) {
    const halo = ctx.e('span', 'creative-finish-halo');
    halo.setAttribute('aria-hidden', 'true');
    host.append(halo);
    temporary.add(halo);
    const animation = animate(
      halo,
      [
        { opacity: 0, transform: 'scale(.94)' },
        { opacity: 0.85, transform: 'scale(1)', offset: 0.25 },
        { opacity: 0, transform: 'scale(1.04)' },
      ],
      750,
    );
    if (animation)
      void animation.finished.then(
        () => {
          halo.remove();
          temporary.delete(halo);
        },
        () => {},
      );
    else {
      halo.remove();
      temporary.delete(halo);
    }
  }
  function sand(previous, next) {
    const added = next.data.layers.length - previous.data.layers.length;
    if (added <= 0) return;
    const art = board.querySelector('.creative-sand');
    if (!art) return;
    const token = sandGeneration;
    const origin = board.getBoundingClientRect(),
      rect = art.getBoundingClientRect();
    // SVG preserveAspectRatio can letterbox inside the full card. Rim and band
    // coordinates use the actual300×360 artwork, not the outer element bounds.
    const scale = Math.min(rect.width / 300, rect.height / 360);
    const insetX = (rect.width - 300 * scale) / 2;
    const insetY = (rect.height - 360 * scale) / 2;
    const count = Math.min(72, 24 + added * 12);
    for (let i = 0; i < count; i++) {
      const layer = previous.data.layers.length + Math.floor((i * added) / count);
      const grain = ctx.e('span', 'creative-pour-grain');
      grain.setAttribute('aria-hidden', 'true');
      grain.dataset.layer = String(layer);
      grain.style.setProperty('--creative-ink', `var(--game-colour-${next.data.layers[layer]})`);
      grain.style.setProperty('--grain-size', `${1.6 + (i % 3) * 0.45}px`);
      grain.style.left = `${rect.left - origin.left + insetX + 300 * scale * (0.5 + ((i % 5) - 2) * 0.008)}px`;
      grain.style.top = `${rect.top - origin.top + insetY + 13 * scale}px`;
      board.append(grain);
      temporary.add(grain);
      const landing = scale * (337 - layer * 12 - 13);
      const drift = ((i % 9) - 4) * 300 * scale * 0.035;
      const frames = Array.from({ length: 9 }, (_, sample) => {
        const t = sample / 8;
        return {
          transform: `translate(${drift * t * t}px, ${landing * t * t}px)`,
          opacity: sample === 0 || sample === 8 ? 0 : 1,
          offset: t,
        };
      });
      const animation = animate(grain, frames, 420, i * 8, 'linear');
      // One sampled accelerating trajectory never pauses at control points.
      if (animation)
        void animation.finished.then(
          () => {
            grain.remove();
            temporary.delete(grain);
          },
          () => {},
        );
      else {
        grain.remove();
        temporary.delete(grain);
      }
    }
    let last;
    for (let i = previous.data.layers.length; i < next.data.layers.length; i++) {
      const band = art.querySelector(`[data-sand-band="${i}"]`);
      last = animate(
        band,
        [
          { opacity: 0, transform: 'scaleX(.04) scaleY(.5)' },
          { opacity: 1, transform: 'scaleX(.25) scaleY(1.14)', offset: 0.24 },
          { opacity: 1, transform: 'scaleX(.82) scaleY(1.04)', offset: 0.7 },
          { opacity: 1, transform: 'scale(1)' },
        ],
        400,
        330 + (i - previous.data.layers.length) * 150,
      );
      if (band) band.style.transformOrigin = 'center bottom';
    }
    if (
      next.data.layers.length === next.data.capacity &&
      previous.data.layers.length < previous.data.capacity &&
      last
    )
      void last.finished.then(
        () => {
          if (
            token === sandGeneration &&
            !disposed &&
            !document.hidden &&
            board.isConnected &&
            !window.matchMedia('(prefers-reduced-motion: reduce)').matches
          )
            glow(board);
        },
        () => {},
      );
  }
  function painted(previous, next) {
    next.data.board.forEach((value, index) => {
      if (value < 0 || value === previous.data.board[index]) return;
      if (next.gameId === 'mosaic-paint') {
        const tile = cell(index);
        animate(
          tile?.querySelector('[data-mosaic-glaze]') || tile,
          [
            { transform: 'scale(.25)', opacity: 0.35 },
            { transform: 'scale(1.02)', opacity: 1, offset: 0.75 },
            { transform: 'scale(1)', opacity: 1 },
          ],
          220,
        );
        return;
      }
      const artwork = cell(index);
      animate(
        next.gameId === 'pattern-stamp'
          ? artwork?.querySelector('[data-print-impression]') || artwork
          : artwork,
        next.gameId === 'pattern-stamp'
          ? [
              { transform: 'scale(.96)', filter: 'brightness(.75)', opacity: 0.25 },
              { transform: 'scale(.995)', filter: 'brightness(1.12)', opacity: 1, offset: 0.45 },
              { transform: 'scale(1)', filter: 'brightness(1)', opacity: 1 },
            ]
          : [
              { filter: 'brightness(1.7)', opacity: 0.4 },
              { filter: 'brightness(1)', opacity: 1 },
            ],
        240,
      );
    });
    if (next.gameId === 'mosaic-paint') {
      const before = gameView(previous).progress,
        after = gameView(next).progress;
      if (after?.current === after?.total && after.total > 0 && before?.current < before?.total)
        glow(board);
    }
  }
  function paperPreview(previous, next) {
    const before = gameView(previous).scene;
    const after = gameView(next).scene;
    after.print.forEach((value, index) => {
      if (value < 0 || value === before.print[index]) return;
      animate(
        scene.querySelector(`[data-paper-cell="${index}"] [data-paper-mark]`),
        [
          { transform: 'scale(.3)', opacity: 0.3 },
          { transform: 'scale(1)', opacity: 1 },
        ],
        240,
      );
    });
    for (const [enabled, old, names, axis, origin] of [
      [after.vertical, before.vertical, ['tr', 'br'], 'X', 'left center'],
      [after.horizontal, before.horizontal, ['bl', 'br'], 'Y', 'center top'],
    ]) {
      if (!enabled || old) continue;
      for (const name of names) {
        const panel = scene.querySelector(`[data-paper-quadrant="${name}"]`);
        animate(
          panel,
          [
            { transform: `scale${axis}(.04)`, filter: 'brightness(1.12)' },
            { transform: `scale${axis}(1)`, filter: 'brightness(1)' },
          ],
          360,
        );
        if (panel) panel.style.transformOrigin = origin;
      }
    }
  }
  function finishCreation(next) {
    const id = next.gameId;
    const world = scene.querySelector('.creative-world');
    const frames = {
      'sand-layers': [{ filter: 'brightness(1.35)' }, { filter: 'brightness(1)' }],
      'mosaic-paint': [
        { filter: 'brightness(1.25)', opacity: 1 },
        { filter: 'brightness(1)', opacity: 1 },
      ],
      'pattern-stamp': [
        { opacity: 0.72, filter: 'saturate(1.4)' },
        { opacity: 1, filter: 'saturate(1)' },
      ],
      'ripple-garden': [{ filter: 'brightness(1.25)' }, { filter: 'brightness(1)' }],
      'thread-loom': [{ filter: 'brightness(1.45)' }, { filter: 'brightness(1)' }],
      'cloud-shapes': [
        { opacity: 0.8, filter: 'brightness(1.18)' },
        { opacity: 1, filter: 'brightness(1)' },
      ],
      'paper-fold': [
        { transform: 'scaleX(.94)', opacity: 0.85 },
        { transform: 'scaleX(1)', opacity: 1 },
      ],
    }[id];
    if (!frames) return;
    const sceneFinish = ['ripple-garden', 'cloud-shapes', 'paper-fold'].includes(id);
    const artwork = sceneFinish && world ? [world] : [...board.querySelectorAll('.game-piece-art')];
    artwork.forEach((node, index) =>
      animate(
        node,
        frames,
        460,
        ['mosaic-paint', 'thread-loom'].includes(id) ? (index % 6) * 25 : 0,
      ),
    );
    glow(sceneFinish && world ? scene : board);
  }
  function balance(previous, next) {
    if (JSON.stringify(previous.data.board) === JSON.stringify(next.data.board)) return;
    const before = pebbleBalanceGeometry(gameView(previous).scene.imbalance);
    const after = pebbleBalanceGeometry(gameView(next).scene.imbalance);
    const beam = scene.querySelector('[data-scale-angle]');
    animate(
      beam,
      [
        { transform: `rotate(${before.angle}deg)` },
        {
          transform: `rotate(${after.angle + (after.angle - before.angle) * 0.08}deg)`,
          offset: 0.72,
        },
        { transform: `rotate(${after.angle}deg)` },
      ],
      420,
    );
    for (const endpoint of after.trays) {
      const old = before.trays.find((tray) => tray.side === endpoint.side);
      animate(
        scene.querySelector(`[data-tray-motion="${endpoint.side}"]`),
        [
          { transform: `translate(${old.x - endpoint.x}px,${old.y - endpoint.y}px)` },
          {
            transform: `translate(${(endpoint.x - old.x) * 0.05}px,${(endpoint.y - old.y) * 0.05}px)`,
            offset: 0.72,
          },
          { transform: 'translate(0,0)' },
        ],
        420,
      );
    }
    for (let index = 0; index < next.data.board.length; index++) {
      if (!next.data.board[index] || next.data.board[index] === previous.data.board[index])
        continue;
      animate(
        scene.querySelector(`[data-placed-pebble="${index}"]`),
        [
          { transform: 'translateY(-8px)', opacity: 0.6 },
          { transform: 'translateY(1px)', opacity: 1, offset: 0.7 },
          { transform: 'translateY(0)', opacity: 1 },
        ],
        300,
      );
    }
    if (!previous.completed && next.completed) glow(scene);
  }
  function clouds(previous, next) {
    const changed = next.data.board.flatMap((value, index) =>
      value !== previous.data.board[index] ? [index] : [],
    );
    if (!changed.length) return;
    const old = ctx.e('span', 'creative-cloud-transition');
    old.setAttribute('aria-hidden', 'true');
    // Reuse the canonical authorised renderer; never reconstruct rules or add
    // cloud pixels to engine state. One previous scene dissolves into acceptance.
    paintSceneArtwork(ctx, old, gameView(previous), previous);
    scene.append(old);
    temporary.add(old);
    const transition = animate(old, [{ opacity: 1 }, { opacity: 0 }], 360);
    if (transition)
      void transition.finished.then(
        () => {
          old.remove();
          temporary.delete(old);
        },
        () => {},
      );
    else {
      old.remove();
      temporary.delete(old);
    }
    for (const index of changed)
      if (next.data.board[index])
        animate(
          scene.querySelector(`[data-cloud-cell="${index}"]`),
          [{ opacity: 0 }, { opacity: 1 }],
          480,
        );
  }

  function loom(previous, next) {
    const origin = board.getBoundingClientRect();
    for (const axis of ['weft', 'warp']) {
      const before = axis === 'weft' ? previous.data.rows : previous.data.columns;
      const after = axis === 'weft' ? next.data.rows : next.data.columns;
      for (let thread = 0; thread < 6; thread++) {
        if (after[thread] === before[thread]) continue;
        const indices = Array.from({ length: 6 }, (_, step) =>
          axis === 'weft' ? thread * 6 + step : step * 6 + thread,
        );
        for (const [step, index] of indices.entries())
          animate(
            cell(index)?.querySelector(`[data-thread-axis="${axis}"]`),
            [{ filter: 'brightness(1.45)' }, { filter: 'brightness(1)' }],
            420,
            step * 45,
          );
        const first = cell(indices[0])?.getBoundingClientRect(),
          last = cell(indices.at(-1))?.getBoundingClientRect();
        if (!first || !last || first.width < 1 || first.height < 1) continue;
        const shuttle = ctx.e('span', 'creative-thread-shuttle');
        shuttle.setAttribute('aria-hidden', 'true');
        shuttle.dataset.threadAxis = axis;
        shuttle.style.setProperty('--shuttle-colour', `var(--game-colour-${after[thread]})`);
        Object.assign(shuttle.style, {
          left: `${first.left - origin.left + first.width / 2 - (axis === 'weft' ? 14 : 5)}px`,
          top: `${first.top - origin.top + first.height / 2 - (axis === 'weft' ? 5 : 14)}px`,
          width: axis === 'weft' ? '28px' : '10px',
          height: axis === 'weft' ? '10px' : '28px',
        });
        board.append(shuttle);
        temporary.add(shuttle);
        const animation = animate(
          shuttle,
          [
            { transform: 'translate(0,0)', opacity: 0 },
            {
              transform: `translate(${(last.left - first.left) * 0.1}px,${(last.top - first.top) * 0.1}px)`,
              opacity: 1,
              offset: 0.1,
            },
            {
              transform: `translate(${last.left - first.left}px,${last.top - first.top}px)`,
              opacity: 0,
            },
          ],
          690,
          0,
          'cubic-bezier(.2,.5,.5,1)',
        );
        if (animation)
          void animation.finished.then(
            () => {
              shuttle.remove();
              temporary.delete(shuttle);
            },
            () => {},
          );
        else {
          shuttle.remove();
          temporary.delete(shuttle);
        }
      }
    }
  }

  function play(previous, next) {
    cancel();
    if (
      disposed ||
      document.hidden ||
      !board.isConnected ||
      previous.gameId !== next.gameId ||
      !supported.has(next.gameId) ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    )
      return;
    const id = next.gameId;
    if (!previous.completed && next.completed && next.data.completion === 'creation') {
      finishCreation(next);
      return;
    }
    if (id === 'sand-layers') sand(previous, next);
    if (['mosaic-paint', 'pattern-stamp', 'paper-fold'].includes(id)) painted(previous, next);
    if (id === 'paper-fold') paperPreview(previous, next);
    if (id === 'ripple-garden' && next.data.serial > previous.data.serial) {
      const ring = scene.querySelector(`[data-serial="${next.data.serial}"]`);
      animate(
        ring?.querySelector('.pond-waves'),
        [
          { transform: 'scale(.12)', opacity: 1 },
          { transform: 'scale(.65)', opacity: 0.8, offset: 0.28 },
          { transform: 'scale(1.65)', opacity: 0 },
        ],
        1650,
        208,
        'cubic-bezier(.16,.6,.36,1)',
      );
      animate(
        ring?.querySelector('.pond-pebble'),
        [
          { transform: 'translateY(-18px) scale(.6)', opacity: 0 },
          { transform: 'translateY(0) scale(1)', opacity: 1, offset: 0.65 },
          { transform: 'translateY(2px) scale(.35)', opacity: 0 },
        ],
        320,
      );
    }
    if (id === 'cloud-shapes') clouds(previous, next);
    if (id === 'thread-loom') loom(previous, next);
    if (id === 'pebble-balance' && next.data.weights) balance(previous, next);
  }
  const hidden = () => {
    if (document.hidden) cancel();
  };
  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  const motionChanged = () => {
    if (media.matches) cancel();
  };
  document.addEventListener('visibilitychange', hidden);
  media.addEventListener?.('change', motionChanged);
  return {
    play,
    cancel,
    dispose() {
      if (disposed) return;
      disposed = true;
      cancel();
      document.removeEventListener('visibilitychange', hidden);
      media.removeEventListener?.('change', motionChanged);
    },
  };
}
